import { useCallback } from 'react';

import { DEFAULT_CURRENCY } from '@/data/config/DEFAULTS';
import { useSettings } from '@/data/Settings';
import { getExternalId } from '@/data/utils/externalId';
import { PinterestClientPayload } from '@/pages/api/tracking/pinterest/pinterestAPIUtils';
import { useCurrentPhysicalStoreId } from 'presentation/events/hooks/useCurrentPhysicalStoreId';
import { useTrackingStoreLocation } from 'presentation/events/hooks/useTrackingStoreLocation';
import { usePinterestClickIdentifiers } from 'presentation/events/hooks/useClickIdentifiers';
import { triggerPinterestCAPIEvent } from 'presentation/events/services/pinterestService';
import { safeEncodeUrl } from 'presentation/events/utils/ga4Utils';
import { getItemPartNumber } from 'presentation/events/utils/productUtils';
import { emptyToNull } from 'presentation/events/utils/stringUtils';
import { useGetCategories } from 'shared/custom/hooks/Categories';

import { SendMediaEventProps, TrackViewItemProduct } from './types';
import { calculateProductsValue, getProductCategoryString, getViewItemProductData } from './utils';

export const useTrackViewItemPinterest = () => {
	const { settings } = useSettings();
	const { navigation } = useGetCategories();
	const { physicalStoreId } = useCurrentPhysicalStoreId();
	const storeLocation = useTrackingStoreLocation();
	const { epik } = usePinterestClickIdentifiers();

	const { userData, storeId } = settings;

	const sendPinterestEvent = useCallback(
		({ eventId, products, bundleId, personInfo }: SendMediaEventProps) => {
			if (userData?.ENABLE_PINTEREST_CALL !== 'true') return;
			if (!physicalStoreId) return;

			const isBundle = !!bundleId;
			const contentIds = products.map((product) => getItemPartNumber(product));
			const value = calculateProductsValue(products, physicalStoreId);
			const singleProduct = products[0] as TrackViewItemProduct;

			const contents = !isBundle
				? undefined
				: products.map((product) => {
						const {
							id,
							quantity,
							price,
							categoryString: item_category,
						} = getViewItemProductData(product, physicalStoreId, navigation);
						return {
							id,
							item_name: product.name ?? '',
							item_price: price.toString(),
							item_brand: product.manufacturer,
							item_category,
							quantity,
						};
				  });

			const singleProductData = isBundle
				? {}
				: {
						content_name: singleProduct.name,
						content_brand: singleProduct.manufacturer,
						content_category: getProductCategoryString(singleProduct, navigation),
				  };

			const pinterestPayload: PinterestClientPayload = {
				event_name: 'page_visit',
				event_id: eventId,
				storeId,
				event_source_url: safeEncodeUrl(window.location.href),
				epik,
				test: userData?.ENABLE_PINTEREST_TEST === 'true',
				custom_data: {
					content_ids: contentIds,
					contents,
					...singleProductData,
					currency: DEFAULT_CURRENCY,
					value: value.toString(),
					externalId: getExternalId(),
					email: emptyToNull(personInfo?.email),
					firstName: emptyToNull(personInfo?.firstName),
					lastName: emptyToNull(personInfo?.lastName ?? null),
					phone: emptyToNull(personInfo?.phone ?? null),
					city: emptyToNull(storeLocation?.city ?? null),
					state: emptyToNull(storeLocation?.state ?? null),
					zip: emptyToNull(storeLocation?.zipCode ?? null),
				},
			};

			triggerPinterestCAPIEvent(pinterestPayload);
		},
		[
			userData?.ENABLE_PINTEREST_CALL,
			userData?.ENABLE_PINTEREST_TEST,
			physicalStoreId,
			storeLocation,
			storeId,
			epik,
			navigation,
		]
	);

	return { sendPinterestEvent };
};
